'use client';

import React__default, { useState, useImperativeHandle, useEffect } from 'react';
import { CalendarContext } from './context/calendar.js';
import { ECalendarType, ECalendarStyle } from './types.js';
export { ECalendarStyle, ECalendarType } from './types.js';
import { CalendarWrapper, CalendarContainer, CalendarFooter } from './styles.css.js';
import HeaderCalendar from './components/header.js';
import CalendarRangePanel from './components/rangePanel.js';
import CalendarPanel from './components/panel.js';
import { TODAY, FIRST_DAY } from './const.js';
import { ELanguage } from '../Utils/constants.js';
import { getFullYear, getMonth, getDate } from './utils/calendar.js';
import Button from '../Button/index.js';

var Calendar = React__default.forwardRef(function CalendarComponent(_a, ref) {
    var _b;
    var initDates = _a.initDates, _c = _a.type, type = _c === void 0 ? ECalendarType.month : _c, _d = _a.pickerType, pickerType = _d === void 0 ? ECalendarStyle.single : _d, _e = _a.calendarStyle, calendarStyle = _e === void 0 ? ECalendarStyle.single : _e, min = _a.min, max = _a.max, handleDateClick = _a.handleDateClick, _f = _a.langCode, langCode = _f === void 0 ? ELanguage.EN : _f, disabled = _a.disabled, rangeYears = _a.rangeYears;
    var _g = useState(type), calendarType = _g[0], setCalendarType = _g[1];
    var _h = useState((_b = (initDates && initDates[0])) !== null && _b !== void 0 ? _b : new Date()), currentDate = _h[0], setCurrentDate = _h[1];
    var _j = useState(initDates !== null && initDates !== void 0 ? initDates : []), selectedDates = _j[0], setSelectedDates = _j[1];
    var _k = useState(getFullYear(currentDate)), year = _k[0], setYear = _k[1];
    var _l = useState(getMonth(currentDate)), month = _l[0], setMonth = _l[1];
    var _m = useState([]), temporaryDates = _m[0], setTemporaryDates = _m[1];
    var onPicker = function (date) {
        if (!date) {
            setCurrentDate(new Date());
            setSelectedDates([]);
            return;
        }
        var selectedStartMonth = getMonth(date);
        var selectedStartYear = getFullYear(date);
        var currentMonth = getMonth(currentDate);
        var currentYear = getFullYear(currentDate);
        if (selectedStartMonth !== currentMonth || selectedStartYear !== currentYear) {
            setCurrentDate(date);
        }
        var datesRange = selectedDates;
        var startDate = datesRange[0];
        var endDate = datesRange[1];
        switch (pickerType) {
            case ECalendarStyle.range:
                if ((!endDate && date > startDate) || date > endDate) {
                    handleDateClick([startDate, date]);
                    setSelectedDates([startDate, date]);
                }
                else if (!endDate && date < startDate) {
                    handleDateClick([date, startDate]);
                    setSelectedDates([date, startDate]);
                }
                else if (datesRange && datesRange.length > 1 && date < startDate) {
                    handleDateClick([date, endDate]);
                    setSelectedDates([date, endDate]);
                }
                else {
                    handleDateClick([date]);
                    setSelectedDates([date]);
                }
                break;
            default:
                handleDateClick([date]);
                setSelectedDates([date]);
                break;
        }
    };
    useImperativeHandle(ref, function () { return ({ onPicker: onPicker }); });
    var goToNextMonth = function () {
        setCurrentDate(function (prevDate) {
            var currentMonth = getMonth(prevDate);
            var currentYear = getFullYear(prevDate);
            var nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
            var nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
            return new Date(nextYear, nextMonth, FIRST_DAY);
        });
    };
    var goToPreviousMonth = function () {
        setCurrentDate(function (prevDate) {
            var currentMonth = getMonth(prevDate);
            var currentYear = getFullYear(prevDate);
            var previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
            var previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
            return new Date(previousYear, previousMonth, FIRST_DAY);
        });
    };
    var goToNextYear = function () {
        setCurrentDate(function (prevDate) {
            var nextYear = getFullYear(prevDate) + 1;
            var currentMonth = getMonth(prevDate);
            return new Date(nextYear, currentMonth, FIRST_DAY);
        });
    };
    var goToPreviousYear = function () {
        setCurrentDate(function (prevDate) {
            var previousYear = getFullYear(prevDate) - 1;
            var currentMonth = getMonth(prevDate);
            return new Date(previousYear, currentMonth, FIRST_DAY);
        });
    };
    var checkSameDate = function () {
        try {
            if (!selectedDates || selectedDates.length < 1) {
                return false;
            }
            var isSameDate_1 = new Date(getFullYear(TODAY), getMonth(TODAY), getDate(TODAY), 0, 0, 0).toDateString() ===
                new Date(getFullYear(selectedDates[0]), getMonth(selectedDates[0]), getDate(selectedDates[0]), 0, 0, 0).toDateString();
            return isSameDate_1;
        }
        catch (error) {
            console.error(error);
        }
    };
    var isSameDate = checkSameDate() ? checkSameDate() : false;
    useEffect(function () {
        setSelectedDates(initDates !== null && initDates !== void 0 ? initDates : []);
        setCurrentDate(initDates && initDates.length > 0 ? initDates[0] : new Date());
    }, [initDates]);
    var contextValue = {
        type: type,
        langCode: langCode,
        minDate: min,
        maxDate: max,
        calendarStyle: calendarStyle,
        pickerType: pickerType,
        currentDate: currentDate,
        setCurrentDate: setCurrentDate,
        selectedDates: selectedDates,
        setSelectedDates: setSelectedDates,
        handleDatesClick: onPicker,
        year: year,
        setYear: setYear,
        calendarType: calendarType,
        setCalendarType: setCalendarType,
        temporaryDates: temporaryDates,
        setTemporaryDates: setTemporaryDates,
        month: month,
        setMonth: setMonth,
        rangeYears: rangeYears
    };
    return (React__default.createElement(CalendarContext.Provider, { value: contextValue },
        React__default.createElement(CalendarWrapper, null,
            calendarType === ECalendarType.combine || calendarType === ECalendarType.combineMonthAndYear ? null : (React__default.createElement(HeaderCalendar, { onClickNext: goToNextMonth, onClickPrev: goToPreviousMonth, onClickSupperNext: goToNextYear, onClickSupperPrev: goToPreviousYear })),
            React__default.createElement(CalendarContainer, { disabled: disabled },
                calendarStyle === ECalendarStyle.range ? React__default.createElement(CalendarRangePanel, null) : React__default.createElement(CalendarPanel, null),
                calendarType !== ECalendarType.month || isSameDate ? null : (React__default.createElement(CalendarFooter, null,
                    React__default.createElement(Button, { outline: 'none', size: 'md', type: 'info', onClick: function () {
                            if ((max && TODAY > max) || (min && TODAY < min)) {
                                setCurrentDate(TODAY);
                                setYear(TODAY.getFullYear());
                                return;
                            }
                            setSelectedDates([TODAY]);
                            setCurrentDate(TODAY);
                            setYear(TODAY.getFullYear());
                            onPicker(TODAY);
                        } }, langCode === ELanguage.EN ? 'Today' : 'Hôm nay')))))));
});
var Calendar$1 = React__default.memo(Calendar);

export { Calendar$1 as default };
//# sourceMappingURL=index.js.map
